import { SceneContext } from '@/lib/threeScene/SceneContext';
import { Scene } from 'three';
import { stop, stopAll, type StopHandle } from '@/util/stopHandle';
import { updateNode } from '@/lib/threeScene/updateNode';
import { reactive, watch } from 'vue';
import type {
    HipSpineXraySceneNode,
    HipSpineXraySceneState,
} from '@/views/3d-planner/hipSpine/scene/hipSpineSceneState';
import { watchImmediate } from '@vueuse/core';
import {
    type HipSpinePostureViewState,
    useHipSpineStore,
} from '@/views/3d-planner/hipSpine/hipSpineStore';
import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
import {
    fullViewCameraParams,
    XRAY_CAMERA_TARGET_DISTANCE,
} from '@/views/dicoms/xrays/xraySceneState';
import { vector3 } from '@/domain/geometry/vector3';
import type { AnnotationGroup } from '@/views/3d-planner/hipSpine/types';
import { updateLandmarkMarkerScale } from '@/views/3d-planner/hipSpine/scene/landmarkMarkerScale';

export class HipSpineXraySceneContext extends SceneContext<
    HipSpineXraySceneNode,
    HipSpineXraySceneState
> {}

export function hipSpineXraySceneContext(state: HipSpineXraySceneState): HipSpineXraySceneContext {
    const scene = new Scene();
    const sceneState = reactive(state);
    scene.up.set(0, 0, 1);
    return new HipSpineXraySceneContext(scene, sceneState);
}

/**
 * Register every node of the supplied groups with the renderer.
 * Returns a handle that tears down each registration in reverse order
 * — children are removed before their parents, which prevents
 * removeNode from reparenting (mutating persistent node.parent).
 */
function registerGroupNodes<T extends AnnotationGroup>(
    context: HipSpineXraySceneContext,
    groups: T[],
): StopHandle {
    const handles: StopHandle[] = [];
    for (const group of groups) {
        handles.push(updateNode(context, group.sceneGroup));
        for (const node of group.children) {
            handles.push(updateNode(context, node));
        }
    }
    return () => {
        for (let i = handles.length - 1; i >= 0; i--) handles[i]();
    };
}

/**
 * Watch a single posture view-state and keep the scene in sync with it.
 * Annotation groups are pre-built on the view state — we just attach them to the scene
 * and register their nodes with the Three.js renderer via updateNode.
 *
 * The registration watchers fire when `viewState.annotationGroups` or
 * `viewState.angleGroups` is reassigned (e.g. by `rebuildPostureGeometry` after a
 * landmark drag ends), tearing down the old node handles and registering the new
 * groups so the scene reflects the rebuilt geometry.
 */
export function applyPanelVisibility(viewState: HipSpinePostureViewState, panelView: string): void {
    const editVm = useHipSpineXRayEditViewModel();
    const showAnnotations = panelView === 'annotations';
    const showAngles = panelView === 'angles';
    // A group renders only when its tab is active AND the surgeon hasn't hidden
    // it. Visibility intent is owned by the edit ViewModel, so it is the single
    // source of truth: this is the only writer of annotation `sceneGroup.visible`.
    for (const group of viewState.annotationGroups) {
        group.sceneGroup.visible =
            showAnnotations && !editVm.isGroupHidden(viewState.posture, group);
    }
    for (const group of viewState.angleGroups) {
        const angleId = String(group.sceneGroup.id).replace(/^angle-/, '');
        const angle = viewState.angles.find((a) => a.id === angleId);
        group.sceneGroup.visible = showAngles && (angle?.visible ?? true);
    }
}

/**
 * Write the surgeon's brightness/contrast adjustments onto a posture's image
 * material uniforms. The single writer of these uniforms — re-applied per posture
 * so the global adjustment follows whichever radiograph is shown.
 */
export function applyImageAdjustments(
    viewState: HipSpinePostureViewState,
    adjustments: { brightness: number; contrast: number },
): void {
    viewState.imageMaterial.uniforms.uBrightness.value = adjustments.brightness;
    viewState.imageMaterial.uniforms.uContrast.value = adjustments.contrast;
}

/**
 * Keep the image plane sized/positioned and the camera fitted to the current
 * radiograph. Extracted from `updateFromViewState` so that function stays a
 * readable list of the per-view-state watchers.
 */
function watchImagePlaneAndCamera(
    context: HipSpineXraySceneContext,
    viewState: HipSpinePostureViewState,
): StopHandle {
    const imagePlane = context.state.nodes.imagePlane;
    const camera = context.state.rendering.camera;
    return watchImmediate(
        () => [viewState.imageSize, context.state.rendering.viewportSize] as const,
        ([imageSize, viewportSize]) => {
            if (imageSize === null) {
                imagePlane.visible = false;
                return;
            }
            imagePlane.material = viewState.imageMaterial;
            imagePlane.width = imageSize.width;
            imagePlane.height = imageSize.height;
            imagePlane.transform.setPosition(imageSize.width / 2, imageSize.height / 2, 0);
            imagePlane.visible = true;

            if (viewportSize) {
                const { target, zoom } = fullViewCameraParams(imageSize, viewportSize);
                camera.zoom = zoom;
                camera.target.copy(target);
                camera.position.copy(
                    target.clone().add(vector3(0, 0, XRAY_CAMERA_TARGET_DISTANCE)),
                );
            }
        },
    );
}

function updateFromViewState(
    context: HipSpineXraySceneContext,
    viewState: HipSpinePostureViewState,
    store: ReturnType<typeof useHipSpineStore>,
): StopHandle {
    const editVm = useHipSpineXRayEditViewModel();
    let annotationHandle: StopHandle = registerGroupNodes(context, viewState.annotationGroups);
    let angleHandle: StopHandle = registerGroupNodes(context, viewState.angleGroups);
    context.state.nodes.annotationGroups = viewState.annotationGroups;
    context.state.nodes.angleGroups = viewState.angleGroups;

    return stopAll(
        watchImagePlaneAndCamera(context, viewState),
        // Landmark markers hold a constant on-screen size; the image keeps zooming.
        updateLandmarkMarkerScale(context.state.rendering, viewState),
        // Apply the global brightness/contrast adjustment to this posture's material.
        // The adjustment intent is projected onto `context.state.imageAdjustments`
        // by the image-controls ViewModel — the scene reads it through state and
        // never imports the store. Re-runs on posture switch (this whole function
        // re-runs), so the active radiograph always reflects the current values.
        watchImmediate(
            () =>
                [
                    context.state.imageAdjustments.brightness,
                    context.state.imageAdjustments.contrast,
                ] as const,
            () => applyImageAdjustments(viewState, context.state.imageAdjustments),
        ),
        // Toggle visibility: annotations vs angles
        watchImmediate(
            () => store.activePanelView,
            (panelView) => applyPanelVisibility(viewState, panelView),
        ),
        // Sync individual angle visibility from sidebar toggles
        watchImmediate(
            () => viewState.angles.map((a) => a.visible),
            () => applyPanelVisibility(viewState, store.activePanelView),
        ),
        // Re-apply when the surgeon hides/shows an annotation group (no rebuild).
        // Without this, a plain visibility toggle would not reach the scene until
        // the next rebuild or panel switch.
        watch(
            () => editVm.hiddenGroupKeys(viewState.posture),
            () => applyPanelVisibility(viewState, store.activePanelView),
        ),
        // Re-register annotation groups when the array is reassigned
        // (e.g. by rebuildPostureGeometry after a landmark drag ends).
        // The watcher must run before any render; flush 'sync' guarantees the
        // tear-down + re-register happens immediately on assignment, before
        // Three.js can render with stale or disposed nodes. Apply visibility
        // after re-registration so newly built groups respect the active panel
        // view (otherwise rebuilt groups default to visible=true and angle
        // overlays leak onto the annotations tab).
        watch(
            () => viewState.annotationGroups,
            (newGroups) => {
                annotationHandle();
                annotationHandle = registerGroupNodes(context, newGroups);
                context.state.nodes.annotationGroups = newGroups;
                applyPanelVisibility(viewState, store.activePanelView);
            },
            { flush: 'sync' },
        ),
        // Re-register angle groups on reassignment.
        watch(
            () => viewState.angleGroups,
            (newGroups) => {
                angleHandle();
                angleHandle = registerGroupNodes(context, newGroups);
                context.state.nodes.angleGroups = newGroups;
                applyPanelVisibility(viewState, store.activePanelView);
            },
            { flush: 'sync' },
        ),
        // Cleanup: stop node watchers (removes Three.js objects from scene)
        // but does NOT dispose geometry/material — they persist on the view state nodes.
        () => annotationHandle(),
        () => angleHandle(),
    );
}

/**
 * Set up the hip-spine xray scene: image plane, annotation groups, and camera fitting.
 */
export function updateHipSpineXrayScene(context: HipSpineXraySceneContext): StopHandle {
    const store = useHipSpineStore();
    const imagePlane = context.state.nodes.imagePlane;
    let update: StopHandle | null = null;

    return stopAll(
        updateNode(context, imagePlane),
        watchImmediate(
            () => store.activePostureId,
            (postureId) => {
                stop(update);

                if (!postureId) {
                    imagePlane.visible = false;
                    context.state.nodes.annotationGroups = [];
                    context.state.nodes.angleGroups = [];
                    return;
                }

                const viewState = store.viewState(postureId);
                if (viewState) {
                    update = updateFromViewState(context, viewState, store);
                }
            },
        ),
        () => stop(update),
    );
}
